import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, Trash2, Copy, Check, X, ChevronUp,
  Save, Calculator, RotateCcw, MoreVertical, Edit3
} from 'lucide-react';
import { clientService } from '../services/api';
import cuttingPlanService from '../services/cuttingPlanService';
import api from '../services/api';
import BoardPatternSelector from './BoardPatternSelector';
import toast from 'react-hot-toast';

const CuttingPlanEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState({
    plan_code: 'PLN-000001',
    description: '',
    client_id: '',
    notes: '',
    trim_margin: 10
  });
  const [pieces, setPieces] = useState([]);
  const [boardPatterns, setBoardPatterns] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    loadBoardPatterns();
    loadClients();
    if (isEditing) {
      loadPlan(id);
    }
  }, [id]);

  const loadBoardPatterns = async () => {
    try {
      const response = await api.get('/board-patterns?limit=1000');
      if (response.data.success) {
        setBoardPatterns(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar padrões:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientService.getClients({ limit: 1000 });
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadPlan = async (planId) => {
    setLoading(true);
    try {
      const result = await cuttingPlanService.getPlanById(planId);
        if (result.success) {
          const planData = result.data;
          setPlan({
            plan_code: planData.plan_code,
            description: planData.description || '',
            client_id: planData.client_id || '',
            notes: planData.notes || ''
          });
          if (planData.pieces_data) {
            const parsed = typeof planData.pieces_data === 'string'
              ? JSON.parse(planData.pieces_data)
              : planData.pieces_data;
            if (Array.isArray(parsed)) {
              setPieces(parsed.map((p, i) => ({
                ...p,
                enabled: true,
                id: `piece_${i}`,
                number: i + 1,
                rotation_allowed: !!p.rotation_allowed
              })));
            }
          }
        }
      } catch (error) {
      console.error('Erro ao carregar plano:', error);
      toast.error('Erro ao carregar plano');
    } finally {
      setLoading(false);
    }
  };

  const addPiece = () => {
    const newPiece = {
      id: `temp_${Date.now()}`,
      number: pieces.length + 1,
      enabled: true,
      quantity: 1,
      length: 0,
      width: 0,
      description: '',
      board_pattern_id: '',
      board_pattern_name: '',
      rotation_allowed: true,
      environment: '',
      created: true
    };
    setPieces([...pieces, newPiece]);
  };

  const updatePiece = (index, field, value) => {
    const updated = [...pieces];
    updated[index][field] = value;
    if (field === 'length' || field === 'width') {
      updated[index][field] = parseFloat(value) || 0;
    }
    if (field === 'quantity') {
      updated[index][field] = parseInt(value) || 1;
    }
    setPieces(updated);
  };

  const deletePiece = (index) => {
    const updated = pieces.filter((_, i) => i !== index);
    updated.forEach((p, i) => p.number = i + 1);
    setPieces(updated);
  };

  const duplicatePiece = (index) => {
    const piece = { ...pieces[index], id: `temp_${Date.now()}`, created: true };
    const updated = [...pieces];
    updated.splice(index + 1, 0, piece);
    updated.forEach((p, i) => p.number = i + 1);
    setPieces(updated);
  };

  const clearPiece = (index) => {
    const updated = [...pieces];
    updated[index] = {
      ...updated[index],
      quantity: 1,
      length: 0,
      width: 0,
      description: '',
      board_pattern_id: '',
      board_pattern_name: '',
      rotation_allowed: true,
      environment: ''
    };
    setPieces(updated);
  };

  const swapDimensions = (index) => {
    const updated = [...pieces];
    const temp = updated[index].length;
    updated[index].length = updated[index].width;
    updated[index].width = temp;
    setPieces(updated);
  };

  const totals = {
    lines: pieces.length,
    totalPieces: pieces.reduce((sum, p) => sum + (p.enabled ? p.quantity : 0), 0),
    totalArea: pieces.reduce((sum, p) => {
      if (!p.enabled) return sum;
      const areaM2 = (p.length * p.width * p.quantity) / 1000000;
      return sum + areaM2;
    }, 0),
    differentPatterns: new Set(pieces.filter(p => p.enabled && p.board_pattern_id).map(p => p.board_pattern_id)).size
  };

  const handleSave = async () => {
    if (!plan.description.trim()) {
      toast.error('Descrição do plano é obrigatória');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        description: plan.description,
        client_id: plan.client_id || null,
        notes: plan.notes,
        pieces: pieces.filter(p => p.enabled).map(p => ({
          quantity: p.quantity,
          length: p.length,
          width: p.width,
          description: p.description,
          board_pattern_id: p.board_pattern_id || null,
          rotation_allowed: p.rotation_allowed,
          environment: p.environment
        }))
      };

      if (isEditing) {
        await api.put(`/cutting-plans/${id}`, payload);
        toast.success('Plano atualizado com sucesso');
      } else {
        const response = await api.post('/cutting-plans', payload);
        if (!response.data.success) throw new Error('Falha ao criar plano');
        toast.success('Plano criado com sucesso');
      }

      navigate('/cutting-plans');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate('/cutting-plans');
  };

  const handleCalculate = async () => {
    const enabledPieces = pieces.filter(p => p.enabled && p.length > 0 && p.width > 0);

    if (enabledPieces.length === 0) {
      toast.error('Adicione pelo menos uma peça com dimensões válidas');
      return;
    }

    if (!plan.description?.trim()) {
      toast.error('Informe a descrição do plano antes de calcular');
      return;
    }

    setSaving(true);
    try {
      // 1. Salvar plano primeiro
      let planId = id;
      const payload = {
        description: plan.description,
        client_id: plan.client_id || null,
        notes: plan.notes,
        pieces: enabledPieces.map(p => ({
          quantity: p.quantity,
          length: p.length,
          width: p.width,
          description: p.description,
          board_pattern_id: p.board_pattern_id || null,
          rotation_allowed: !!p.rotation_allowed,
          environment: p.environment
        }))
      };

      if (isEditing) {
        await api.put(`/cutting-plans/${id}`, payload);
      } else {
        const createResponse = await api.post('/cutting-plans', payload);
        if (createResponse.data?.success) {
          planId = createResponse.data.data.id;
          toast.success('Plano criado');
        } else {
          throw new Error('Falha ao criar plano');
        }
      }

      // 2. Gerar plano de corte
      const partsPayload = enabledPieces.map((p, idx) => ({
        partCode: `PIECE-${planId}-${idx + 1}`,
        partName: p.description || `Peça ${idx + 1}`,
        width: Math.max(parseFloat(p.length) || 0, parseFloat(p.width) || 0),
        height: Math.min(parseFloat(p.length) || 0, parseFloat(p.width) || 0),
        thickness: 18,
        materialName: p.board_pattern_name || 'MDF',
        materialId: null,
        boardPatternId: p.board_pattern_id || null,
        rotationAllowed: p.rotation_allowed !== false,
        quantity: p.quantity || 1,
        environment: p.environment || ''
      }));

      const calcResponse = await api.post(`/cutting-plans/${planId}/generate`, {
        parts: partsPayload,
        boardWidth: 2750,
        boardHeight: 1840,
        boardThickness: 18,
        trimMargin: parseFloat(plan.trim_margin) || 0
      });

      if (calcResponse.data?.success) {
        const { stats } = calcResponse.data.data;
        toast.success(
          `✅ ${stats.totalPositions} peças em ${stats.totalSheets} chapas • Eficiência: ${stats.efficiency}%` +
          (stats.mode ? ` • Modo: ${stats.mode}` : '')
        );

        setTimeout(() => {
          navigate(`/cutting-plans/${planId}/result`);
        }, 1500);
      } else {
        toast.error(calcResponse.data?.message || 'Erro ao gerar plano');
      }
    } catch (error) {
      console.error('Erro ao calcular:', error);
      toast.error(error.response?.data?.message || 'Erro ao calcular plano de corte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">
              {isEditing ? `Editar Plano: ${plan.plan_code}` : 'Novo Plano de Corte'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Fechar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar Plano'}
            </button>
            <button
              onClick={handleCalculate}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Calculator className="w-4 h-4" />
              {saving ? 'Calculando...' : 'Calcular Corte'}
            </button>
          </div>
        </div>
      </div>

      {/* Plan Info */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
            <input
              type="text"
              value={plan.plan_code}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição do Plano *</label>
            <input
              type="text"
              value={plan.description}
              onChange={(e) => setPlan({ ...plan, description: e.target.value })}
              placeholder="Ex: Cozinha Cliente João"
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <select
              value={plan.client_id}
              onChange={(e) => setPlan({ ...plan, client_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sem cliente</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" title="Limpeza de fábrica nas 4 bordas da chapa de MDF (Corte Certo usa 10mm)">
              Refilo / Margem (mm)
            </label>
            <input
              type="number"
              min="0"
              max="50"
              value={plan.trim_margin}
              onChange={(e) => setPlan({ ...plan, trim_margin: Math.max(0, parseFloat(e.target.value) || 0) })}
              placeholder="Ex: 10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <input
              type="text"
              value={plan.notes}
              onChange={(e) => setPlan({ ...plan, notes: e.target.value })}
              placeholder="Ex: MDF Branco 18mm"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Pieces Table */}
      <div className="flex-1 p-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-10">Nº</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-12">OK</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-20">Qtd</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-22">Comp (mm)</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-10">↔</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-22">Larg (mm)</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase min-w-[160px]">Descrição</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase w-56">Padrão de Chapa</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-14" title="Permitir girar 90° (desmarque para peças com veio de madeira fixo)">Girar</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase w-28">Ambiente</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-500 uppercase w-18">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pieces.map((piece, index) => (
                  <tr key={piece.id} className={piece.enabled ? '' : 'bg-gray-50 text-gray-400'}>
                    <td className="px-2 py-1 text-center font-medium text-gray-500">
                      {piece.number}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={piece.enabled}
                        onChange={(e) => updatePiece(index, 'enabled', e.target.checked)}
                        className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={piece.quantity}
                        onChange={(e) => updatePiece(index, 'quantity', e.target.value)}
                        min={1}
                        className="w-full px-1.5 py-0.5 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={piece.length || ''}
                        onChange={(e) => updatePiece(index, 'length', e.target.value)}
                        placeholder="mm"
                        className="w-full px-1.5 py-0.5 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => swapDimensions(index)}
                        className="p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title="Trocar dimensões"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={piece.width || ''}
                        onChange={(e) => updatePiece(index, 'width', e.target.value)}
                        placeholder="mm"
                        className="w-full px-1.5 py-0.5 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={piece.description}
                        onChange={(e) => updatePiece(index, 'description', e.target.value)}
                        placeholder="Descrição"
                        className="w-full px-1.5 py-0.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <BoardPatternSelector
                        value={piece.board_pattern_id}
                        onChange={(patternId, pattern) => {
                          const updated = [...pieces];
                          updated[index].board_pattern_id = patternId || '';
                          updated[index].board_pattern_name = pattern?.description || '';
                          setPieces(updated);
                        }}
                        patterns={boardPatterns}
                        onRefresh={loadBoardPatterns}
                        placeholder="Selecionar..."
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={!!piece.rotation_allowed}
                        onChange={(e) => updatePiece(index, 'rotation_allowed', e.target.checked)}
                        className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={piece.environment || ''}
                        onChange={(e) => updatePiece(index, 'environment', e.target.value)}
                        className="w-full px-1.5 py-0.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="">Selecionar...</option>
                        <option value="COZINHA">Cozinha</option>
                        <option value="QUARTO">Quarto</option>
                        <option value="SALA">Sala</option>
                        <option value="BANHEIRO">Banheiro</option>
                        <option value="ESCRITORIO">Escritório</option>
                        <option value="AREA_SERVICO">Área de Serviço</option>
                        <option value="LAVABO">Lavabo</option>
                        <option value="CLOSET">Closet</option>
                        <option value="HALL">Hall</option>
                        <option value="VARANDA">Varanda</option>
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          onClick={() => duplicatePiece(index)}
                          className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Duplicar"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => clearPiece(index)}
                          className="p-0.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Limpar"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deletePiece(index)}
                          className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Excluir"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pieces.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                      Nenhuma peça cadastrada. Clique no botão "+" abaixo para adicionar peças.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Add Button */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={addPiece}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Adicionar Peça
            </button>
            {pieces.length > 0 && (
              <span className="text-sm text-gray-500">
                {pieces.length} peça(s) cadastrada(s)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer with Totals */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex items-center justify-end gap-8">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Linhas cadastradas:</span>
            <span className="font-semibold text-gray-900">{totals.lines}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Total de peças:</span>
            <span className="font-semibold text-gray-900">{totals.totalPieces}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Área total:</span>
            <span className="font-semibold text-gray-900">{totals.totalArea.toFixed(4)} m²</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Padrões diferentes:</span>
            <span className="font-semibold text-gray-900">{totals.differentPatterns}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CuttingPlanEditor;
